<template>
  <div class="autocomplete-input">
    <TextInput
      :id="id"
      :value="value"
      :label="label"
      :error="error"
      :disabled="disabled"
      :autofocus="autofocus"
      @update:value="$emit('update:value', $event)"
      @focus="showPredictions = true"
      @blur="onBlur"
      @keydown="onKeydown"
    >
      <template slot="pre">
        <div v-if="showPredictions" class="predictions">
          <a
            v-for="(prediction, idx) in predictions"
            :key="idx"
            :class="[
              'prediction',
              idx === highlightedPrediction && 'highlighted',
            ]"
            @mousedown.prevent="selectPrediction(prediction)"
          >
            {{ prediction.value }}
          </a>
        </div>
      </template>
      <template slot="right">
        <slot />
      </template>
    </TextInput>
  </div>
</template>

<style lang="scss">
.autocomplete-input {
  .text-input input {
    padding-right: rem(45px);
  }

  .input-icon {
    position: absolute;
    right: rem(20px);
    top: 50%;
    width: rem(16px);
    height: rem(16px);
    font-size: rem(20px);
    transform: translateY(-50%);
  }

  .predictions {
    position: absolute;
    width: 100%;
    top: 100%;
    left: 0;
    @include setColor(--color-background1, background-color);
    font-size: rem(14px);
    z-index: 2;
    box-shadow: 0px 2px 24px rgba(0, 0, 0, 0.1);

    .prediction {
      display: block;
      height: rem(35px);
      line-height: rem(35px);
      padding: rem(0 15px);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.highlighted,
      &:hover {
        text-decoration: none;
        @include setColor(--color-background2, background-color);
      }
    }
  }
}
</style>

<script>
import debounce from "lodash.debounce";

import TextInput from "../TextInput";

export default {
  components: { TextInput },

  props: {
    id: { type: String, required: true },
    value: { type: String, default: "" },
    predictions: { type: Array, default: () => [] },
    label: { type: String, required: false },
    autofocus: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: null },
    predictionsInterval: { type: Number, default: 200 },
  },

  data() {
    return {
      showPredictions: false,
      highlightedPrediction: null,
      recentPrediction: null,
    };
  },

  watch: {
    value() {
      this.onValueChangedDebounced();
    },
  },

  methods: {
    async onValueChanged() {
      // Do not request predictions if value has been set from a prediction
      const recentPrediction =
        this.recentPrediction && this.recentPrediction.value;
      if (this.value === recentPrediction) {
        this.$emit("clearPredictions");
        return;
      }

      if (!this.value) {
        this.$emit("clearPredictions");
      }
      this.$emit("debouncedValueChange");
    },

    onBlur() {
      this.showPredictions = false;
      if (this.recentPrediction) {
        this.$emit("update:value", this.recentPrediction.value);
      }

      this.$emit("blur");
    },

    async selectPrediction(prediction) {
      this.highlightedPrediction = null;
      this.recentPrediction = prediction;

      this.$emit("select", prediction);
    },

    highlightPrevPrediction() {
      if (this.highlightedPrediction) {
        this.highlightedPrediction--;
      }
    },

    highlightNextPrediction() {
      if (this.highlightedPrediction === null) {
        this.highlightedPrediction = 0;
      } else {
        this.highlightedPrediction = Math.min(
          this.predictions.length - 1,
          this.highlightedPrediction + 1,
        );
      }
    },

    onKeydown(event) {
      switch (event.keyCode) {
        case 38: // arrow up
          return this.highlightPrevPrediction();
        case 40: // arrow down
          return this.highlightNextPrediction();
        case 13: // enter
        case 9: // tab
          if (this.highlightedPrediction !== null) {
            event.preventDefault();
            return this.selectPrediction(
              this.predictions[this.highlightedPrediction],
            );
          }
      }
    },
  },

  created() {
    this.onValueChangedDebounced =
      this.predictionsInterval !== 0
        ? debounce(this.onValueChanged, this.predictionsInterval)
        : this.onValueChanged;
  },
};
</script>
